import { Avatar } from '@startuptools/ui/Avatar';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Trans } from '@lingui/react';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveUpIcon from '@mui/icons-material/MoveUp';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import EditIcon from '@mui/icons-material/Edit';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { GqlAgendaItem } from '../../../../graphql/base-types.graphql';
import { canMove } from './utils';
import { Tooltip } from '@startuptools/ui/Tooltip';

export const AgendaTable = ({
  agendaItems,
  secondaryLang,
  movableRange,
  doDelete,
  doMove,
  doEdit,
  loading,
}: {
  agendaItems: Pick<
    GqlAgendaItem,
    'id' | 'order' | 'subjectPrimaryLang' | 'subjectSecondaryLang' | 'firstGroup' | 'lastGroup' | 'mandatory'
  >[];
  secondaryLang: boolean;
  movableRange: number[];
  doDelete: (id: string) => Promise<unknown>;
  doMove: (id: string, newOrder: number) => Promise<unknown>;
  doEdit?: (id: string) => unknown;
  loading: boolean;
}) => {
  return (
    <Table>
      <thead>
        <tr>
          <th style={{ width: 50 }} />
          <th>
            <Trans id="Subject" />
          </th>
          {secondaryLang && (
            <th>
              <Trans id="Subject (eng)" />
            </th>
          )}
          <th style={{ width: 150 }} />
        </tr>
      </thead>
      <tbody>
        {agendaItems.map(ai => {
          const move = canMove(movableRange, ai.order);
          return (
            <tr key={ai.id}>
              <td>
                <Avatar size="sm" variant="outlined">
                  {ai.order}
                </Avatar>
              </td>
              <td>{ai.subjectPrimaryLang}</td>
              {secondaryLang && <td>{ai.subjectSecondaryLang}</td>}
              <td align="right">
                <Stack direction="row" gap={1} justifyContent="end">
                  {move && (
                    <ButtonGroup>
                      <IconButton onClick={() => doMove(ai.id, ai.order - 1)} disabled={!move.up || loading}>
                        <MoveUpIcon />
                      </IconButton>
                      <IconButton onClick={() => doMove(ai.id, ai.order + 1)} disabled={!move.down || loading}>
                        <MoveDownIcon />
                      </IconButton>
                    </ButtonGroup>
                  )}
                  {doEdit && !ai.firstGroup && !ai.lastGroup && (
                    <IconButton variant="outlined" color="primary" onClick={() => doEdit(ai.id)}>
                      <EditIcon />
                    </IconButton>
                  )}
                  {!ai.mandatory ? (
                    <IconButton disabled={loading} variant="outlined" color="danger" onClick={() => doDelete(ai.id)}>
                      <DeleteIcon />
                    </IconButton>
                  ) : (
                    <Tooltip
                      title={<Trans id="Mandatory Agenda Item, can not be deleted" />}
                      disableInteractive={false}
                    >
                      <IconButton variant="outlined" sx={theme => ({ color: theme.palette.neutral[300] })}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
